import { useEffect } from "react";
import { HeaderBanner } from "@/components/Banner";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { ASSUMED_USER_STORAGE_KEY, getInternalReturnUrl } from "@/providers/AssumeUserProvider/storage";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";

const REASON_LABELS: Record<string, string> = {
  ACTOR_NOT_PERMITTED: "You are not permitted to assume users",
  FEATURE_DISABLED: "Assumed-user access is disabled",
  FEATURE_FLAG_UNAVAILABLE: "Assumed-user access is temporarily unavailable",
  INVALID_HEADER: "The assumed-user request was invalid",
  TARGET_NOT_FOUND: "The selected user could not be found",
  TARGET_NOT_CONFIRMED: "The selected user could not be confirmed",
  IMPERSONATION_SETUP_FAILED: "Assumed-user setup failed",
  UNSUPPORTED_PROVIDER: "The selected user is not linked to a supported identity provider",
  INVALID_PROVIDER_IDENTIFIER: "The selected user's identity provider record is incomplete",
  MISSING_AUTHORIZATION_PROFILE: "The selected user's authorization profile is missing",
  MALFORMED_AUTHORIZATION_PROFILE: "The selected user's authorization profile is incomplete",
  NO_M360_ROLES: "The selected user has no active Member 360 backoffice role",
  NO_M360_CAPABILITIES: "The selected user has no active Member 360 capabilities",
  IDENTITY_PROVIDER_UNAVAILABLE: "The identity provider could not be reached"
};

function getReasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, " ").toLowerCase();
}

export default function AssumeUserBanner() {
  const { assumedUser, impersonationError, stopAssumingUser } = useAssumeUser();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (!assumedUser) return;

    const handleAssumedUserStorageChange = (event: StorageEvent) => {
      if (event.key !== null && event.key !== ASSUMED_USER_STORAGE_KEY) return;
      if (event.newValue !== null) return;

      const returnUrl = getInternalReturnUrl(assumedUser.returnUrl);
      stopAssumingUser();
      navigate(returnUrl as Path);
    };

    window.addEventListener("storage", handleAssumedUserStorageChange);
    return () => window.removeEventListener("storage", handleAssumedUserStorageChange);
  }, [assumedUser, navigate, stopAssumingUser]);

  if (impersonationError && !assumedUser) {
    const returnHome = () => {
      stopAssumingUser();
      navigate("/home");
    };
    const tryAgain = () => {
      stopAssumingUser();
      navigate(`/assume-user?return_to=${encodeURIComponent(currentUrl)}` as Path);
    };

    return (
      <HeaderBanner
        variant="error"
        description={impersonationError.message}
        subText={`Reason: ${getReasonLabel(impersonationError.reason)}.`}
        primaryAction={{ label: "Return to home", onClick: returnHome }}
        secondaryAction={impersonationError.retryable ? { label: "Try again", onClick: tryAgain } : undefined}
        testId="assume-user-error"
      />
    );
  }

  if (!assumedUser) return null;

  const stopAssumingUserAndReturn = () => {
    const returnUrl = getInternalReturnUrl(assumedUser.returnUrl);
    stopAssumingUser();
    navigate(returnUrl as Path);
  };

  return (
    <HeaderBanner
      variant="warn"
      inline
      description={`Viewing as ${assumedUser.name} (${assumedUser.email})`}
      primaryAction={{ label: "Stop", onClick: stopAssumingUserAndReturn }}
      hideSecondaryDismiss
      testId="assume-user-banner"
    />
  );
}
